/**
 * iter169-170 — Walk-in booking pricing math (pure helpers).
 *
 * Per-room GST math for the Walk-in Booking flow. Kept as pure functions so
 * they can be unit-tested without React.
 *
 * Product rules (Feb 2026):
 *   • Every room type carries its OWN manually-editable per-night rate.
 *   • GST slab is chosen from the individual PER-ROOM-PER-NIGHT rate:
 *       rate < ₹7,500  → 5%
 *       rate ≥ ₹7,500  → 12%
 *   • Room Charges = base_rate × room_count × nights
 *   • Room GST     = Room Charges × slab%
 *
 * GST modes (iter170):
 *   • 'none'      → GST=0, GSTIN hidden. Room total = rate × count × nights.
 *   • 'excluding' → Rate is EX-tax. GST added on top. Total per night
 *                   = rate × count × (1 + slab/100).
 *   • 'including' → Rate is IN-tax. Extract GST from the rate; the total
 *                   customer payable is EXACTLY rate × count × nights.
 *                   base_rate = rate / (1 + slab/100).
 */

export const GST_SLAB_THRESHOLD = 7500;     // ₹ per room per night
export const GST_SLAB_LOW_PCT   = 5;
export const GST_SLAB_HIGH_PCT  = 12;

export const GST_MODES = Object.freeze({
  NONE:      'none',
  EXCLUDING: 'excluding',
  INCLUDING: 'including',
});

const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;

/** Determine the GST slab % for a per-room-per-night rate. */
export function slabForRate(rate) {
  const r = Number(rate);
  if (!Number.isFinite(r) || r <= 0) return 0;
  return r >= GST_SLAB_THRESHOLD ? GST_SLAB_HIGH_PCT : GST_SLAB_LOW_PCT;
}

/**
 * iter174 — Resolve the effective GST % for a room:
 *   1. If `admin.gst_enabled === false` → 0% (No GST forced).
 *   2. If `admin.gst_percentage` is a finite number ≥ 0 → use it as-is
 *      (flat rate). iter181 — 0% is now a valid admin override so the
 *      panel can explicitly zero-out GST for a property without
 *      falling back to the per-room slab. Only a genuinely absent /
 *      null / non-numeric value triggers the slab fallback.
 *   3. Otherwise → fall back to the per-room-per-night slab.
 *
 * @param {number|string} rate
 * @param {{gst_enabled?: boolean, gst_percentage?: number|null}} [admin]
 */
export function effectiveSlab(rate, admin) {
  if (admin && admin.gst_enabled === false) return 0;
  const raw = admin ? admin.gst_percentage : undefined;
  if (raw !== null && raw !== undefined && raw !== '') {
    const dynamicPct = Number(raw);
    if (Number.isFinite(dynamicPct) && dynamicPct >= 0) return dynamicPct;
  }
  return slabForRate(rate);
}

/** Normalise any legacy/invalid gst_mode value into one of the 3 canonical modes. */
export function normalizeGstMode(mode) {
  if (mode === GST_MODES.EXCLUDING || mode === GST_MODES.INCLUDING) return mode;
  return GST_MODES.NONE;
}

/**
 * Compute a per-room breakdown for a single room selection.
 * @returns {{
 *   room_type: string, room_count: number, rate: number, base_rate: number,
 *   rate_missing: boolean, nights: number,
 *   charges_base: number, charges_per_night: number,
 *   gst_pct: number, gst_amount: number, gst_mode: string,
 *   total_per_night: number, total_charges: number
 * }}
 */
export function computeRoomLine({ room_type, room_count, rate, nights, gst_mode, admin }) {
  // iter174 — Admin toggle short-circuits GST regardless of user mode.
  const forcedNoGst = !!(admin && admin.gst_enabled === false);
  const mode = forcedNoGst ? GST_MODES.NONE : normalizeGstMode(gst_mode);
  const count   = Math.max(1, Number(room_count || 1));
  const rateNum = Number(rate);
  const valid   = Number.isFinite(rateNum) && rateNum > 0;
  const n       = Math.max(0, Number(nights || 0));
  const slabRaw = valid ? effectiveSlab(rateNum, admin) : 0;
  const slab    = mode === GST_MODES.NONE ? 0 : slabRaw;

  let baseRate = 0;
  let chargesBase = 0;
  let gstAmt = 0;
  let totalCharges = 0;
  let totalPerNight = 0;

  if (valid) {
    if (mode === GST_MODES.NONE) {
      baseRate      = rateNum;
      chargesBase   = round2(rateNum * count * n);
      gstAmt        = 0;
      totalCharges  = chargesBase;
      totalPerNight = round2(rateNum * count);
    } else if (mode === GST_MODES.EXCLUDING) {
      baseRate      = rateNum;
      chargesBase   = round2(rateNum * count * n);
      gstAmt        = round2(chargesBase * slab / 100);
      totalCharges  = round2(chargesBase + gstAmt);
      totalPerNight = round2(rateNum * count * (1 + slab / 100));
    } else { // INCLUDING
      baseRate      = round2(rateNum / (1 + slab / 100));
      chargesBase   = round2(baseRate * count * n);
      // Derive GST amount from the difference so rounding stays consistent
      // and the customer-payable total is exactly rate × count × nights.
      totalCharges  = round2(rateNum * count * n);
      gstAmt        = round2(totalCharges - chargesBase);
      totalPerNight = round2(rateNum * count);
    }
  }

  return {
    room_type,
    room_count: count,
    rate: valid ? rateNum : 0,
    base_rate: baseRate,
    rate_missing: !valid,
    nights: n,
    gst_pct: slab,
    gst_mode: mode,
    charges_base: chargesBase,
    charges_per_night: valid ? round2(rateNum * count) : 0,
    gst_amount: gstAmt,
    total_charges: totalCharges,
    total_per_night: totalPerNight,
  };
}

/**
 * Aggregate a booking's per-room lines + totals.
 * @param {Array} rooms — `{room_type|room_name, room_count, rate|room_rate}`
 * @param {number} nights
 * @param {string} gst_mode — 'none' | 'excluding' | 'including'
 * @param {{gst_enabled?: boolean, gst_percentage?: number|null}} [admin]
 *        iter174 — Dynamic Admin-Panel-controlled GST config. When absent,
 *        the legacy per-room slab math applies (backward compatible).
 */
export function computeWalkinPricing(rooms, nights, gst_mode, admin) {
  const forcedNoGst = !!(admin && admin.gst_enabled === false);
  const mode = forcedNoGst ? GST_MODES.NONE : normalizeGstMode(gst_mode);
  const per_room = (rooms || []).map((r) => computeRoomLine({
    room_type:   r.room_type ?? r.room_name ?? '',
    room_count:  r.room_count,
    rate:        r.rate ?? r.room_rate,
    nights,
    gst_mode: mode,
    admin,
  }));
  const room_charges_total = round2(per_room.reduce((s, x) => s + x.charges_base, 0));
  const room_gst_total     = round2(per_room.reduce((s, x) => s + x.gst_amount, 0));
  const room_grand_total   = round2(room_charges_total + room_gst_total);
  const anyLow  = per_room.some((x) => x.gst_pct === GST_SLAB_LOW_PCT);
  const anyHigh = per_room.some((x) => x.gst_pct === GST_SLAB_HIGH_PCT);
  return {
    gst_mode: mode,
    per_room,
    room_charges_total,        // taxable base (rate × count × nights when EX, base × count × nights when IN)
    room_gst_total,
    room_grand_total,          // charges + GST (customer payable for rooms only)
    any_low_slab: anyLow,
    any_high_slab: anyHigh,
    mixed_slabs: anyLow && anyHigh,
    show_gstin: mode !== GST_MODES.NONE,
    // iter174 — Surface the admin-controlled state so callers/UI can hide
    // the picker or show a "GST is disabled by admin" hint.
    admin_gst_enabled: !forcedNoGst,
    // iter181 — Any finite gst_percentage ≥ 0 is a valid admin override
    // (0% must be reported so UI can say "Admin set GST to 0%"). Only a
    // genuinely absent / null / non-numeric value falls back to slab.
    admin_gst_percentage: (() => {
      const raw = admin ? admin.gst_percentage : undefined;
      if (raw === null || raw === undefined || raw === '') return null;
      const n = Number(raw);
      return Number.isFinite(n) && n >= 0 ? n : null;
    })(),
  };
}
